/**
 * i18n очереди модерации компаний-партнёров (DEV-795, редизайн DEV-964).
 *
 * Подмешивается в основные локали через deep-merge в ./index.ts (как agentsAdmin).
 * Блок companyVerifications.* + пункт common.navigation.companyVerifications.
 * Без литеральных фигурных скобок и служебных символов vue-i18n (кроме интерполяции {name}).
 */

type LocaleOverride = Record<string, unknown>

export const companyVerificationsMessages: Record<'ru' | 'en' | 'vn', LocaleOverride> = {
  ru: {
    common: { navigation: { companyVerifications: 'Модерация компаний' } },
    companyVerifications: {
      title: 'Модерация компаний',
      subtitle: 'Приём компаний-партнёров в справочник по заявкам агентов',
      tabs: {
        actionNeeded: 'Требуют действия',
        overdue: 'Просроченные',
        noResult: 'Нет результата',
        mine: 'Мои',
        all: 'Все',
      },
      kpi: {
        actionNeeded: 'Требуют действия',
        noResult: 'Нет результата ИИ',
        acceptedMonth: 'Принято за месяц',
        slaOverdue: 'Просрочено SLA',
      },
      filters: {
        search: 'Поиск',
        searchPlaceholder: 'Название компании…',
        status: 'Статус',
        confidence: 'Уверенность',
        responsible: 'Ответственный',
        anyConfidence: 'Любая',
        reset: 'Сбросить',
      },
      columns: {
        company: 'Компания',
        agent: 'Агент',
        agentChoice: 'Выбор агента',
        confidence: 'Уверен.',
        status: 'Статус',
        responsible: 'Ответственный',
        createdAt: 'Создано',
      },
      status: {
        pending_ai: 'Ждёт ИИ',
        ai_candidates: 'Ждёт приёма',
        ai_no_result: 'Нет результата ИИ',
        agent_selected: 'Агент выбрал',
        manager_review: 'Ждёт приёма',
        confirmed: 'Принято',
        expired: 'Просрочено',
        cancelled: 'Отменено',
      },
      noChoice: 'Агент не выбрал',
      overdueMark: 'SLA',
      accept: {
        title: 'Приём компании',
        titleNamed: 'Приём компании «{name}»',
        typedName: 'Ввод агента',
        agent: 'Агент',
        chooseCompany: 'Выберите компанию',
        candidates: 'Кандидаты',
        agentChoice: 'Выбор агента',
        recommended: 'рекомендовано ИИ',
        source: 'Источник',
        bannerNoChoice: 'Агент не выбрал компанию. Выберите подходящую из найденных ИИ.',
        bannerAgentChose:
          'Агент выбрал компанию. Подтвердите его выбор или выберите другую — тогда укажите причину.',
        noCandidates:
          'ИИ не нашёл кандидатов — примите как частного агента или создайте компанию вручную.',
        overrideReasonLabel: 'Почему меняете выбор агента?',
        overrideReasonPlaceholder: 'Например: агент указал не тот филиал',
        btnAccept: 'Принять компанию',
        btnPrivate: 'Частный агент',
        needReason: 'Укажите причину',
        loadError: 'Не удалось загрузить заявку',
      },
      toast: {
        accepted: 'Компания принята в справочник',
        privateSet: 'Агент отмечен как частный',
        corrected: 'Выбор исправлен',
        error: 'Не удалось выполнить действие',
      },
      empty: {
        title: 'Очередь пуста',
        description: 'Заявки на приём компаний появятся здесь.',
        withFilters: 'По выбранным фильтрам ничего не найдено.',
      },
    },
  },
  en: {
    common: { navigation: { companyVerifications: 'Company moderation' } },
    companyVerifications: {
      title: 'Company moderation',
      subtitle: 'Admit partner companies to the directory from agent requests',
      tabs: {
        actionNeeded: 'Needs action',
        overdue: 'Overdue',
        noResult: 'No result',
        mine: 'Mine',
        all: 'All',
      },
      kpi: {
        actionNeeded: 'Needs action',
        noResult: 'No AI result',
        acceptedMonth: 'Accepted this month',
        slaOverdue: 'SLA overdue',
      },
      filters: {
        search: 'Search',
        searchPlaceholder: 'Company name…',
        status: 'Status',
        confidence: 'Confidence',
        responsible: 'Responsible',
        anyConfidence: 'Any',
        reset: 'Reset',
      },
      columns: {
        company: 'Company',
        agent: 'Agent',
        agentChoice: 'Agent choice',
        confidence: 'Conf.',
        status: 'Status',
        responsible: 'Responsible',
        createdAt: 'Created',
      },
      status: {
        pending_ai: 'Awaiting AI',
        ai_candidates: 'Awaiting admission',
        ai_no_result: 'No AI result',
        agent_selected: 'Agent selected',
        manager_review: 'Awaiting admission',
        confirmed: 'Accepted',
        expired: 'Expired',
        cancelled: 'Cancelled',
      },
      noChoice: 'Agent did not choose',
      overdueMark: 'SLA',
      accept: {
        title: 'Admit company',
        titleNamed: 'Admit company “{name}”',
        typedName: 'Agent input',
        agent: 'Agent',
        chooseCompany: 'Choose a company',
        candidates: 'Candidates',
        agentChoice: 'Agent choice',
        recommended: 'AI recommended',
        source: 'Source',
        bannerNoChoice:
          'The agent has not chosen a company. Pick a suitable one from the AI matches.',
        bannerAgentChose:
          'The agent chose a company. Confirm their choice or pick another — then add a reason.',
        noCandidates:
          'AI found no candidates — accept as a private agent or create a company manually.',
        overrideReasonLabel: 'Why change the agent’s choice?',
        overrideReasonPlaceholder: 'e.g. the agent picked the wrong branch',
        btnAccept: 'Accept company',
        btnPrivate: 'Private agent',
        needReason: 'Provide a reason',
        loadError: 'Failed to load the request',
      },
      toast: {
        accepted: 'Company admitted to the directory',
        privateSet: 'Agent marked as private',
        corrected: 'Choice corrected',
        error: 'Action failed',
      },
      empty: {
        title: 'Queue is empty',
        description: 'Company admission requests will appear here.',
        withFilters: 'Nothing matches the selected filters.',
      },
    },
  },
  vn: {
    common: { navigation: { companyVerifications: 'Kiểm duyệt công ty' } },
    companyVerifications: {
      title: 'Kiểm duyệt công ty',
      subtitle: 'Tiếp nhận công ty đối tác vào danh mục theo yêu cầu của đại lý',
      tabs: {
        actionNeeded: 'Cần xử lý',
        overdue: 'Quá hạn',
        noResult: 'Không có kết quả',
        mine: 'Của tôi',
        all: 'Tất cả',
      },
      kpi: {
        actionNeeded: 'Cần xử lý',
        noResult: 'AI không có kết quả',
        acceptedMonth: 'Đã nhận trong tháng',
        slaOverdue: 'Quá hạn SLA',
      },
      filters: {
        search: 'Tìm kiếm',
        searchPlaceholder: 'Tên công ty…',
        status: 'Trạng thái',
        confidence: 'Độ tin cậy',
        responsible: 'Phụ trách',
        anyConfidence: 'Bất kỳ',
        reset: 'Đặt lại',
      },
      columns: {
        company: 'Công ty',
        agent: 'Đại lý',
        agentChoice: 'Lựa chọn đại lý',
        confidence: 'Tin cậy',
        status: 'Trạng thái',
        responsible: 'Phụ trách',
        createdAt: 'Tạo lúc',
      },
      status: {
        pending_ai: 'Chờ AI',
        ai_candidates: 'Chờ tiếp nhận',
        ai_no_result: 'AI không có kết quả',
        agent_selected: 'Đại lý đã chọn',
        manager_review: 'Chờ tiếp nhận',
        confirmed: 'Đã nhận',
        expired: 'Hết hạn',
        cancelled: 'Đã hủy',
      },
      noChoice: 'Đại lý chưa chọn',
      overdueMark: 'SLA',
      accept: {
        title: 'Tiếp nhận công ty',
        titleNamed: 'Tiếp nhận công ty «{name}»',
        typedName: 'Đại lý nhập',
        agent: 'Đại lý',
        chooseCompany: 'Chọn công ty',
        candidates: 'Ứng viên',
        agentChoice: 'Lựa chọn đại lý',
        recommended: 'AI đề xuất',
        source: 'Nguồn',
        bannerNoChoice: 'Đại lý chưa chọn công ty. Hãy chọn công ty phù hợp từ kết quả AI.',
        bannerAgentChose:
          'Đại lý đã chọn công ty. Xác nhận lựa chọn hoặc chọn công ty khác — khi đó hãy nêu lý do.',
        noCandidates:
          'AI không tìm thấy ứng viên — nhận làm đại lý tư nhân hoặc tạo công ty thủ công.',
        overrideReasonLabel: 'Vì sao thay đổi lựa chọn của đại lý?',
        overrideReasonPlaceholder: 'Ví dụ: đại lý chọn nhầm chi nhánh',
        btnAccept: 'Tiếp nhận công ty',
        btnPrivate: 'Đại lý tư nhân',
        needReason: 'Hãy nêu lý do',
        loadError: 'Không tải được yêu cầu',
      },
      toast: {
        accepted: 'Đã tiếp nhận công ty vào danh mục',
        privateSet: 'Đã đánh dấu đại lý tư nhân',
        corrected: 'Đã chỉnh sửa lựa chọn',
        error: 'Thao tác thất bại',
      },
      empty: {
        title: 'Hàng đợi trống',
        description: 'Các yêu cầu tiếp nhận công ty sẽ xuất hiện ở đây.',
        withFilters: 'Không có kết quả phù hợp với bộ lọc.',
      },
    },
  },
}
